import type { DataImportValidateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response ({ message }) to the GraphQL mutation
 * response shape so consumers branching on the result do not need to know
 * which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  message: string;
}): DataImportValidateMutation => ({
  dataImportValidate: {
    __typename: 'DataImportValidateOutput',
    message: data.message,
  },
});

/**
 * NOTE: the tRPC path is ASYNC — the mutation resolves as soon as the
 * validation pipeline has been kicked off (status moves to 'validating' and
 * the results page's status subscription shows live progress), whereas the
 * legacy Hasura action ran validation synchronously. Consumers must not
 * assume validation is complete when the promise resolves.
 */
export const useDataImportValidateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dataImport.validate.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImports.queryKey(),
        });

        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dataImport.dataImportById.queryKey(),
        });
      },
    }),
  });

  return {
    dataImportValidate: async (
      id: string
    ): Promise<DataImportValidateMutation> => {
      const result = await mutation.mutateAsync({ id });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
